import { Observable, of, tap } from 'rxjs';
import { Point } from '../../models/point';
import { Injectable } from '@angular/core';
import { Store } from '../../services/store';
import { toVoid } from '../../utils/void';
import { sameText } from '../../utils/exact-match';

export interface DropdownState<T> {
  loading: boolean;
  openAt: Point | undefined; // rename to position ???
  search: string | undefined;
  /**
   * The term searchResult answers. Differs from search while the debounce or the
   * request is still pending — Enter never acts on a list that is not the term's (0110).
   */
  searchedTerm: string | undefined;
  searchResult: DropdownItemViewModel<T>[] | undefined;
  /**
   * The row ↑/↓ moved to: an index into searchResult, or searchResult.length for the
   * create row. Undefined until the first arrow key — Enter then decides by exact match.
   */
  highlighted: number | undefined;
  searchFunction: SearchFunc<T> | undefined;
  searchFormatter: SearchFormatter<T>;
  exactMatch: ExactMatch<T> | undefined;
  allowCreate: boolean;
  minSearchLength: number;
}

export type SearchFunc<T> = (search: string) => Observable<T[]>;
export type SearchFormatter<T> = (item: T) => string;
export type ExactMatch<T> = (item: T, term: string) => boolean;

export interface DropdownItemViewModel<T> {
  /**
   * Purely presentational id used for efficient item tracking in for loops.
   */
  id: number;

  /**
   * Label displayed in dropdown item.
   */
  label: string;

  /**
   * Object stored by a dropdown item.
   */
  item: T;
}

/**
 * What Enter takes: an item of the list, the create row, or nothing.
 */
export type DropdownEnterTarget<T> = { item: T } | 'create' | undefined;

const INITIAL_STATE: DropdownState<unknown> = {
  loading: false,
  openAt: undefined,
  search: '',
  searchedTerm: undefined,
  searchResult: undefined,
  highlighted: undefined,
  searchFunction: undefined,
  searchFormatter: item => '' + item,
  exactMatch: undefined,
  allowCreate: false,
  minSearchLength: 3
};

// the list on screen is the answer to the term in the input
function isSettled<T>(state: DropdownState<T>): boolean {
  return state.searchedTerm === state.search;
}

// a pure filter compares the option's label; a picker names its own rule (name or part number)
function exactMatchIndex<T>(state: DropdownState<T>): number {
  const term = state.search ?? '';

  if (!term.trim()) {
    return -1;
  }

  const exactMatch = state.exactMatch;

  return (state.searchResult ?? []).findIndex(vm => exactMatch
    ? exactMatch(vm.item, term)
    : sameText(vm.label, term));
}

/**
 * `+ Utwórz „…”` closes the list only where the consumer creates (JIT BOM), for a typed
 * term the answered list has no exact match for — the catalog already has an exact one.
 */
export function showsCreateRow<T>(state: DropdownState<T>): boolean {
  const term = state.search ?? '';

  return state.allowCreate
    && !!term.trim()
    && term.trim().length >= state.minSearchLength
    && isSettled(state)
    && exactMatchIndex(state) === -1;
}

function rowCount<T>(state: DropdownState<T>): number {
  return (state.searchResult?.length ?? 0) + (showsCreateRow(state) ? 1 : 0);
}

/**
 * Enter takes the highlighted row; with none, the exactly matching item, else the
 * create row. Nothing while the list is still on its way.
 */
export function enterTarget<T>(state: DropdownState<T>): DropdownEnterTarget<T> {
  if (!isSettled(state)) {
    return undefined;
  }

  const items = state.searchResult ?? [];
  const index = state.highlighted ?? exactMatchIndex(state);

  if (index >= 0 && index < items.length) {
    return { item: items[index].item };
  }

  return showsCreateRow(state) ? 'create' : undefined;
}

// provided by DropdownComponent, not root: every dropdown instance owns its state
@Injectable()
export class DropdownStore<T> extends Store<DropdownState<T>> {
  constructor() {
    super(INITIAL_STATE as DropdownState<T>);
  }

  init(searchFunc: SearchFunc<T>, searchFormatter?: SearchFormatter<T>, minSearchLength = 3, exactMatch?: ExactMatch<T>, allowCreate = false): void {
    this.update(state => ({
      ...state,
      searchFunction: searchFunc,
      searchFormatter: searchFormatter ?? state.searchFormatter,
      exactMatch,
      allowCreate,
      minSearchLength
    }));
  }

  open(x: number, y: number): void {
    this.update(state => ({ ...state, openAt: { x, y } }));
  }

  openAt(position: Point | undefined): void {
    this.update(state => ({ ...state, openAt: position }));
  }

  close(): void {
    this.update(state => ({
      ...state,
      openAt: INITIAL_STATE.openAt,
      search: INITIAL_STATE.search,
      searchedTerm: undefined,
      searchResult: undefined,
      highlighted: undefined
    }));
  }

  // ↓ stops at the last row, the create row included
  highlightNext(): void {
    this.update(state => {
      const count = rowCount(state);

      if (count === 0) {
        return state;
      }

      const highlighted = state.highlighted === undefined ? 0 : Math.min(state.highlighted + 1, count - 1);

      return { ...state, highlighted };
    });
  }

  // ↑ past the first row hands the choice back to the typed text
  highlightPrevious(): void {
    this.update(state => {
      if (state.highlighted === undefined) {
        return state;
      }

      return { ...state, highlighted: state.highlighted > 0 ? state.highlighted - 1 : undefined };
    });
  }

  // the term follows the input at once: the input binds to it, and Enter reads it;
  // typing hands the choice back to the text, a new list gets no highlight
  setSearch(search: string): void {
    this.update(state => ({ ...state, search, highlighted: undefined }));
  }

  // the debounced half of a search — the component decides when, the store only fetches
  fetch(search: string): Observable<void> {
    if (search.trim().length < this.state.minSearchLength) {
      // too short to search: no list, and nothing stale from a longer term under it
      this.update(state => ({ ...state, searchedTerm: search, searchResult: undefined, highlighted: undefined }));
      return of(undefined);
    }

    this.update(state => ({ ...state, loading: true }));

    // searched trimmed, as Enter compares: "PRZYCZEPA " must find "Przyczepa" to match it
    return this.state.searchFunction!(search.trim())
      .pipe(
        tap(items => {
          this.update(state => ({
            ...state,
            loading: false,
            searchedTerm: search,
            highlighted: undefined,
            searchResult: items.map((item, i) => ({
              id: i,
              item,
              label: state.searchFormatter(item)
            }))
          }))
        }),
        toVoid()
      );
  }
}
